import React, { ReactNode } from 'react';
import ScrollReveal from './ScrollReveal';

interface FullScreenSectionProps {
  backgroundImage?: string;
  backgroundVideo?: string;
  backgroundOverlay?: boolean;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  className?: string;
  textColor?: 'light' | 'dark';
  scrollIndicator?: boolean;
}

/**
 * Tesla-inspired full-screen section component
 * Perfect for hero sections and product showcases
 */
const FullScreenSection: React.FC<FullScreenSectionProps> = ({
  backgroundImage,
  backgroundVideo,
  backgroundOverlay = true,
  title,
  subtitle,
  children,
  className = '',
  textColor = 'light',
  scrollIndicator = true,
}) => {
  // Determine background style based on image or video
  const bgStyle = backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {};
  
  return (
    <section className={`hero ${className}`} style={{minHeight: '100vh'}}>
      {/* Background */}
      <div className={`hero__bg ${backgroundOverlay ? 'hero__bg--overlay' : ''}`} style={bgStyle}>
        {backgroundVideo && (
          <div className="hero__bg--video">
            <video autoPlay muted loop playsInline>
              <source src={backgroundVideo} type="video/mp4" />
            </video>
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="hero__content">
        <ScrollReveal animation="fade-in-up" delay={200}>
          <h1 className={`hero__title ${textColor === 'dark' ? 'text-primary-900' : 'text-white'}`}>
            {title}
          </h1>
          
          {subtitle && (
            <p className={`hero__subtitle ${textColor === 'dark' ? 'text-primary-600' : 'text-white'}`}>
              {subtitle}
            </p>
          )}
          
          {children && (
            <div className="hero__actions">
              {children}
            </div>
          )}
        </ScrollReveal>
      </div>
      
      {/* Scroll indicator */}
      {scrollIndicator && (
        <div className="hero__scroll">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M12 4V20M12 20L18 14M12 20L6 14" 
              stroke={textColor === 'dark' ? '#172B4D' : '#FFFFFF'} 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </section>
  );
};

export default FullScreenSection;